import { SITE_NAME, SITE_URL } from './constants'
import { routing } from '@/i18n/routing'

type RouteKey = keyof typeof routing.pathnames

/**
 * Bir iç rotayı (örn. '/stocks', '/crypto/[symbol]') locale'in GERÇEK URL yoluna
 * çevirir: localePath('/stocks', 'tr') → '/tr/hisseler'.
 *
 * Breadcrumb şemasındaki `item` URL'i ile sayfanın canonical'ı BİREBİR aynı olmak
 * zorundadır; aksi halde Google şemayı yok sayar (Temmuz 2026 denetimi, AS2).
 * Bu yüzden yol adları elle yazılmaz, tek kaynak olan routing.pathnames'ten türetilir.
 */
export function localePath(
  route: RouteKey,
  locale: string,
  params?: Record<string, string>,
): string {
  const entry = routing.pathnames[route] as string | Record<string, string>
  let path = typeof entry === 'string' ? entry : (entry[locale] ?? entry[routing.defaultLocale])
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      path = path.replace(`[${key}]`, value)
    }
  }
  return path === '/' ? `/${locale}` : `/${locale}${path}`
}

export function organizationSchema(locale: string = 'tr') {
  const isEn = locale === 'en'
  return {
    '@context': 'https://schema.org',
    // NewsMediaOrganization, Organization'dan türetilmiştir ama Google News
    // ve YMYL/finans sinyali için çok daha güçlü bir publisher referansıdır.
    '@type': 'NewsMediaOrganization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo.png`,
      width: 512,
      height: 512,
    },
    description: isEn
      ? 'A financial media platform offering real-time price data, exchange rates, crypto and daily financial news from US, European, Asian and other world markets.'
      : 'ABD, Avrupa, Asya ve dünya borsalarından anlık fiyat verisi, döviz, kripto ve günlük finans haberleri sunan finans medyası platformu.',
    inLanguage: ['tr-TR', 'en-US'],
    sameAs: [
      'https://x.com/borsayacom',
      'https://www.instagram.com/borsayacom',
      'https://www.linkedin.com/in/borsaya-com-38a9973b6/',
    ],
    // E-E-A-T sinyalleri (YMYL/finans için önemli — AEO/GEO ve Google için)
    slogan: isEn ? 'Global markets on a single platform' : 'Küresel piyasalar tek platformda',
    foundingDate: '2026',
    foundingLocation: { '@type': 'Country', name: 'Türkiye' },
    address: { '@type': 'PostalAddress', addressCountry: 'TR' },
    email: 'support@borsaya.com',
    areaServed: ['TR', 'Worldwide'],
    knowsAbout: isEn
      ? ['Stock market', 'Stocks', 'Exchange rates', 'Cryptocurrency', 'Commodities', 'Market indices', 'Financial news', 'Investing']
      : ['Borsa', 'Hisse senedi', 'Döviz kurları', 'Kripto para', 'Emtia', 'Piyasa endeksleri', 'Finans haberleri', 'Yatırım'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@borsaya.com',
      // Dile göre doğru iletişim sayfası: TR → /tr/iletisim, EN → /en/contact
      url: `${SITE_URL}/${locale}/${isEn ? 'contact' : 'iletisim'}`,
      availableLanguage: ['Turkish', 'English'],
    },
  }
}

export function websiteSchema(locale: string = 'tr') {
  const searchPath = locale === 'tr' ? '/tr/haberler' : '/en/news'
  const queryParam = locale === 'tr' ? 'arama' : 'search'

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}${searchPath}?${queryParam}={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }
}

export function newsArticleSchema(article: {
  title: string
  description: string
  publishedAt: string
  url: string
  section: string
  tags: string[]
  lang: string
  imageUrl?: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    description: article.description,
    datePublished: article.publishedAt,
    dateModified: article.publishedAt,
    // Ağustos 2026 denetimi: burada '@type': 'Person' ile isimli bir insan editör
    // beyan ediliyordu. Haber metinleri backend'de IArticleAiClient (Gemini) tarafından
    // üretildiği için bu beyan gerçeğe aykırıydı ve Google'ın "scaled content abuse"
    // politikası kapsamında manuel işlem riski taşıyordu.
    // Doğru beyan: yayıncı kurumun kendisi. Şubat 2026 Discover update'inin aradığı
    // "belirli bir yazar" koşulu Organization ile de karşılanır.
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'NewsMediaOrganization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png`, width: 512, height: 512 },
    },
    ...(article.imageUrl && {
      image: { '@type': 'ImageObject', url: article.imageUrl },
      thumbnailUrl: article.imageUrl,
    }),
    mainEntityOfPage: article.url,
    articleSection: article.section,
    keywords: article.tags.join(', '),
    inLanguage: article.lang === 'tr' ? 'tr-TR' : 'en-US',
    // AEO / sesli yanıt motorları için
    isAccessibleForFree: true,
    speakable: { '@type': 'SpeakableSpecification', cssSelector: ['h1'] },
  }
}

export function collectionPageSchema(page: {
  name: string
  description: string
  url: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: page.name,
    description: page.description,
    url: `${SITE_URL}${page.url}`,
    publisher: { '@type': 'NewsMediaOrganization', name: SITE_NAME, url: SITE_URL },
  }
}

export function faqSchema(items: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  }
}

export function financialProductSchema(product: {
  name: string
  symbol: string
  url: string
  price?: number
  currency?: string
  exchange?: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    name: `${product.name} (${product.symbol})`,
    url: product.url,
    ...(product.exchange && {
      provider: { '@type': 'Organization', name: product.exchange },
    }),
    ...(product.price && product.currency && {
      offers: {
        '@type': 'Offer',
        price: product.price.toString(),
        priceCurrency: product.currency,
      },
    }),
  }
}

/**
 * Meta description'ı SERP sınırına kısaltır (AS4 — Temmuz 2026 denetimi).
 * Backend'in ürettiği haber özetleri 200 karakteri aşabiliyor; Google masaüstünde
 * ~155-160 karakterde kesiyor. Kelime ortasından bölmemek için son boşluktan kırpar.
 */
export function truncateForMeta(text: string, max = 155): string {
  const clean = text.trim().replace(/\s+/g, ' ')
  if (clean.length <= max) return clean
  const cut = clean.slice(0, max - 1)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:!?\-–—]$/, '')}…`
}

/** Göreli yolu mutlak URL'e çevirir; zaten mutlaksa OLDUĞU GİBİ bırakır. */
function absoluteUrl(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `${SITE_URL}${url}`
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      // absoluteUrl: çağıran taraf tam URL geçerse alan adı İKİ KEZ yazılmaz
      // (Temmuz 2026 denetimi AS1 — rehber şablonundaki kritik hata).
      item: absoluteUrl(item.url),
    })),
  }
}

/**
 * Listeleme sayfaları için ItemList (hisse / kripto / aracı kurum listeleri).
 * Haberler ItemList, Rehberler CollectionPage kullanıyordu; bu üç sayfada
 * liste şeması hiç yoktu (Temmuz 2026 denetimi, AS3).
 */
export function itemListSchema(
  items: { name: string; url: string }[],
  listName?: string,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    ...(listName && { name: listName }),
    numberOfItems: items.length,
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      url: absoluteUrl(item.url),
    })),
  }
}

/**
 * JSON-LD'yi <script> içine GÜVENLİ enjekte eder. JSON.stringify, `<` `>` `&`
 * karakterlerini escape ETMEZ — bu da bir başlık "</script>..." içeriyorsa script
 * kırılmasına / XSS'e yol açar. Bu helper o karakterleri unicode-escape'ler.
 * Kullanım: dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
 */
export function safeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
}

/** Haber listesi için ItemList (her öğe NewsArticle) — AEO/GEO için güçlü sinyal */
export function newsListSchema(
  articles: { title: string; slug: string; publishedAt: string; imageUrl?: string }[],
  locale: string,
) {
  // TR'de haber DETAY yolu 'haber' (tekil); 'haberler' liste sayfasıdır.
  // Buradaki 'haberler' ItemList şemasındaki tüm TR makale URL'lerini 404'e
  // gönderiyordu (routing.pathnames: '/news/[slug]' → tr '/haber/[slug]').
  const newsPath = locale === 'en' ? 'news' : 'haber'
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: articles.map((a, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'NewsArticle',
        headline: a.title,
        url: `${SITE_URL}/${locale}/${newsPath}/${a.slug}`,
        datePublished: a.publishedAt,
        ...(a.imageUrl && { image: a.imageUrl }),
        publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
      },
    })),
  }
}

/**
 * Anasayfa SSS içeriği — TEK kaynak: hem görünür <details> bölümü hem de
 * FAQPage JSON-LD aynı diziyi kullanır (Google görünür-içerik kuralına uygun).
 */
export function homeFaqItems(locale: string): { q: string; a: string }[] {
  if (locale === 'en') {
    return [
      { q: 'Is Borsaya.com free?', a: 'Yes, Borsaya.com is completely free. You can track index, stock, forex, crypto and commodity prices and financial news without any membership.' },
      { q: 'Which markets can I follow?', a: 'You can follow stocks and indices from the US (NYSE, NASDAQ), Europe, Asia and other world exchanges, plus forex pairs, cryptocurrencies and commodities.' },
      { q: 'How often is the price data updated?', a: 'Prices update in near real-time during market hours via a live WebSocket feed, while historical end-of-day data is synced daily.' },
      { q: 'Where do the crypto prices come from?', a: 'Crypto prices and market caps come from CoinGecko, while stock, forex and commodity data come from TwelveData and Yahoo Finance.' },
      { q: 'Can I see live exchange rates?', a: 'Yes, you can track major currency pairs such as USD/TRY and EUR/USD with live rates and charts.' },
    ]
  }
  return [
    { q: 'Borsaya.com ücretsiz mi?', a: 'Evet, Borsaya.com tamamen ücretsizdir. Endeks, hisse, döviz, kripto ve emtia fiyatlarını ve finans haberlerini üyelik gerekmeden takip edebilirsiniz.' },
    { q: 'Hangi piyasaları takip edebilirim?', a: 'ABD (NYSE, NASDAQ), Avrupa, Asya ve diğer dünya borsalarından hisse ve endeksleri; döviz pariteleri, kripto paralar ve emtia fiyatlarını takip edebilirsiniz.' },
    { q: 'Fiyat verileri ne sıklıkla güncelleniyor?', a: 'Fiyatlar piyasa saatlerinde canlı WebSocket akışı ile gerçek zamanlıya yakın güncellenir; günlük geçmiş veriler her gün senkronize edilir.' },
    { q: 'Kripto para fiyatları nereden geliyor?', a: 'Kripto fiyatları ve piyasa değerleri CoinGecko’dan; hisse, döviz ve emtia verileri TwelveData ve Yahoo Finance’tan alınır.' },
    { q: 'Döviz kurlarını canlı görebilir miyim?', a: 'Evet, USD/TRY, EUR/USD gibi başlıca döviz paritelerini canlı kur ve grafiklerle anlık takip edebilirsiniz.' },
  ]
}
